// REST client for the Carrier Appetite Guide feedback loop.
// (The streaming recommendation lives in graphApi.ts.)

export type AppetiteSentiment = 'LIKE' | 'DISLIKE';
export type AppetiteOutcome = 'ACCEPTED' | 'DECLINED';

export interface AppetiteCarrier {
  carrier: string;
  score: number;
  marketType?: string | null;
  confidence?: string | null;
  learnedAcceptanceRate?: number | null;
  outcomeSampleSize: number;
  sentiment?: AppetiteSentiment | null;
  feedbackText?: string | null;
  outcome?: AppetiteOutcome | null;
}

export interface AppetiteFeedbackPayload {
  quoteId: string;
  carrier: string;
  sentiment?: AppetiteSentiment | null;
  feedbackText?: string | null;
  outcome?: AppetiteOutcome | null;
  recommendationScore?: number | null;
}

function apiBase(): string {
  return process.env.REACT_APP_TRUCKING_PORTAL_AI_API_Url ?? '';
}

function authHeaders(): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('igtoken') || ''}`,
  };
}

/** Ranked carrier shortlist for a prospect, merged with any saved feedback. */
export async function getAppetiteCarriers(quoteId: string): Promise<AppetiteCarrier[]> {
  const res = await fetch(`${apiBase()}/ai/appetite/carriers`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ quoteId }),
  });
  if (!res.ok) throw new Error(`API error ${res.status}: ${res.statusText}`);
  const data = await res.json();
  return (data?.carriers as AppetiteCarrier[]) ?? [];
}

/** Persist like/dislike, free-text, and outcome for one carrier. */
export async function saveAppetiteFeedback(payload: AppetiteFeedbackPayload): Promise<void> {
  const res = await fetch(`${apiBase()}/ai/appetite/feedback`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`API error ${res.status}: ${res.statusText}`);
}

export interface SavedRecommendation {
  found: boolean;
  question?: string | null;
  answer?: string | null;
  graphVersion?: number;
}

/** Load the cached recommendation for a prospect (no LLM). found=false when none. */
export async function getSavedRecommendation(quoteId: string): Promise<SavedRecommendation> {
  const res = await fetch(`${apiBase()}/ai/appetite/recommendation`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ quoteId }),
  });
  if (!res.ok) throw new Error(`API error ${res.status}: ${res.statusText}`);
  return res.json();
}
